import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { z } from "zod/v4";
import { apiFailure } from "#/modules/util.errors/api-failure.server";
import { getSchoolDataByBrin } from "#/modules/school/api.school.server";
import {
  addSport,
  getAdminSporten,
  removeSport,
} from "#/modules/sportdeelname/api.sport-admin.server";
import { adminMiddleware } from "#/modules/util.auth/middleware.admin";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";

/** GET /admin/sportdeelname/sporten — the sport catalogue managed by admins. */
export const getAdminSportsOverview = createServerFn({ method: "GET" })
  .middleware([adminMiddleware])
  .handler(async () => {
    const result = await getAdminSporten();
    if (Result.isError(result)) throw result.error;
    return { sporten: result.value.sporten ?? [] };
  });

/** POST — add a sport. Mirrors portal's `api.add-sport`. */
export const addSportFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator(z.object({ name: z.string().min(1) }))
  .handler(async ({ data }) => {
    const result = await addSport(data.name.trim());
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

/** POST — remove a sport. Mirrors portal's `api.remove-sport`. */
export const removeSportFn = createServerFn({ method: "POST" })
  .middleware([adminMiddleware, toastErrorMiddleware])
  .validator(z.object({ sportId: z.string().min(1) }))
  .handler(async ({ data }) => {
    const result = await removeSport(data.sportId);
    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });

/**
 * POST — search DUO schools by BRIN (for the "add school" form's autofill). Returns `[]` for
 * out-of-range BRIN lengths. Mirrors portal's `admin.schools.api.search-brin`.
 */
export const searchSchoolByBrin = createServerFn({ method: "POST" })
  .middleware([adminMiddleware])
  .validator(z.object({ searchBrin: z.string() }))
  .handler(async ({ data }) => {
    const brin = data.searchBrin.trim();
    if (brin.length < 4 || brin.length > 6) {
      return { schools: [] };
    }
    const result = await getSchoolDataByBrin(brin);
    return { schools: result.unwrapOr(undefined) ?? [] };
  });
